import { useEffect, useMemo, useRef, useState } from "react";
import { StreamingMarkdown, TextContent } from "./markdownStreaming";

const CHARS_PER_FRAME = 10;

/**
 * Renderiza el contenido de UN turno conversacional, exclusivamente la
 * parte de pregunta + respuesta del asistente. Las notas relacionadas
 * se manejan aparte en el panel lateral derecho (`list-wrapper-results`).
 *
 * IMPORTANTE: este componente NO renderiza el wrapper `.summary-card`,
 * solo el contenido. El wrapper común vive en `Results` para que toda
 * la conversación quede dentro de un único contenedor (estilo ChatGPT).
 */
export default function TurnContent({ turn, parentBase, isLast }) {
  const { query, resume, isStreaming, notesReady } = turn;

  const [fullResume, setFullResume] = useState(isStreaming ? "" : resume || "");
  const [showStreaming, setShowStreaming] = useState(!!isStreaming);

  const streamingDomRef = useRef(null);
  const indexRef = useRef(0);
  const rafRef = useRef(null);
  const targetTextRef = useRef("");
  const prevStreamingRef = useRef(isStreaming);
  const resumeRef = useRef(resume);
  useEffect(() => { resumeRef.current = resume; }, [resume]);

  const displayMarkdown = useMemo(() => fullResume || resume || "", [fullResume, resume]);
  const streamingSource = resume || "";

  // Inicio de streaming
  useEffect(() => {
    if (isStreaming && !prevStreamingRef.current) {
      setShowStreaming(true);
      setFullResume("");
      indexRef.current = 0;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }
    prevStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // Tick del typing imperativo
  useEffect(() => {
    if (!isStreaming) return;

    targetTextRef.current = streamingSource;

    const tick = () => {
      indexRef.current = Math.min(indexRef.current + CHARS_PER_FRAME, targetTextRef.current.length);
      const slice = targetTextRef.current.slice(0, indexRef.current);

      if (streamingDomRef.current?.__updateMarkdown) {
        streamingDomRef.current.__updateMarkdown(slice);
      }

      if (indexRef.current < targetTextRef.current.length) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (indexRef.current < targetTextRef.current.length) {
      rafRef.current = requestAnimationFrame(tick);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [streamingSource, isStreaming]);

  // Fin de streaming → fijar contenido estático
  useEffect(() => {
    if (!isStreaming && showStreaming) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setFullResume(resumeRef.current || "");
      setShowStreaming(false);
    }
  }, [isStreaming, showStreaming]);

  // Indicador "..." mientras esperamos la respuesta del último turno
  const showWaitingDots = isLast && (isStreaming || (!resume && !notesReady));

  return (
    <article className="conversation-turn" aria-label={`Turno: ${query}`}>
      {/* Pregunta del usuario - mantiene la estructura original de summary-title-wrap */}
      <header className="summary-header turn-question">
        <div className="summary-title-wrap">
          <h3 className="summary-title">{query}</h3>
          <span className="summary-icon" aria-hidden="true">
            <svg
              className="summary-icon-svg"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
              fill="none"
              stroke="#333333"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7 3h10a2 2 0 0 1 2 2v16l-7-4-7 4V5a2 2 0 0 1 2-2z" />
              <path d="M12 9.2l.35.95.95.35-.95.35-.35.95-.35-.95-.95-.35.95-.35.35-.95z" />
            </svg>
          </span>
        </div>
      </header>

      {/* Respuesta del asistente */}
      <div className="summary-text-wrap turn-answer">
        {showStreaming && (
          <StreamingMarkdown targetRef={streamingDomRef} parentBase={parentBase} />
        )}

        {!showStreaming && displayMarkdown && (
          <TextContent markdown={displayMarkdown} parentBase={parentBase} />
        )}

        {showWaitingDots && (
          <span
            className="streaming-indicator"
            title="Recibiendo datos en tiempo real"
            aria-label="Escribiendo"
          >
            ...
          </span>
        )}
      </div>
    </article>
  );
}